// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

// Auth for the provider dashboard. Email-first home-realm discovery: the
// signing-in user's email (or an org-slug fallback) resolves to a Firebase
// tenant + IdP via the backend, the SDK is pinned to that tenant, then a
// federated REDIRECT runs. On first login the session is bound + claims stamped
// via POST /v1/auth/bootstrap. The dashboard serves both client workforce and
// QH/Quantino operators — operators additionally get a cross-org switcher.
//
// Why redirect (not popup): under Safari ITP + Chrome storage partitioning a
// popup can authenticate in a partitioned storage context and leave the parent
// window signed-out (silent failure). A top-level redirect returns first-party.
// This requires the Firebase auth handler to be same-origin (the app's OWN
// domain), with /__/auth reverse-proxied to <project>.firebaseapp.com (see
// nginx.conf) — a cross-host handler breaks the session handoff under Safari 16.1+.
//
// MFA: TOTP second factor is NOT enrolled in the pilot. MFA-over-redirect (via
// getRedirectResult's auth/multi-factor-auth-required) is a future addition.

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import {
  onAuthStateChanged,
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  OAuthProvider,
  SAMLAuthProvider,
  signOut,
  getIdTokenResult,
  type Auth,
  type AuthProvider as FirebaseAuthProvider,
  type User,
} from 'firebase/auth'
import {
  auth,
  isFirebaseConfigured,
  TENANT_STORAGE_KEY,
  ACTING_AS_STORAGE_KEY,
} from '@/lib/firebase'
import {
  bootstrapSession,
  resolveTenant,
  setApiOrgContext,
  getApiOrgContext,
} from '@/lib/api'
import { isOperator as roleIsOperator, isOrgAdmin } from '@/lib/access'
import type { OrgOption, Role, TenantResolution } from '@/types'

const VALID_ROLES: ReadonlySet<string> = new Set<Role>([
  'platform_superadmin',
  'qh_admin',
  'org_admin',
  'clinic_staff',
  'provider',
])

function isRole(v: unknown): v is Role {
  return typeof v === 'string' && VALID_ROLES.has(v)
}

// Clears the pending-tenant pin from localStorage (best-effort).
function clearPendingTenant(): void {
  try {
    localStorage.removeItem(TENANT_STORAGE_KEY)
  } catch {
    // localStorage unavailable — nothing to clear.
  }
}

// Maps a resolver IdP id to a Firebase auth provider. Returns null for an
// unsupported id so the caller can surface a clear error rather than guessing.
// Federated provider ids are prefixed per Firebase convention: SAML uses
// `saml.<id>` (→ SAMLAuthProvider) and OIDC uses `oidc.<id>` (→ OAuthProvider).
// `loginHint` (the email the user typed) is passed through as the OAuth
// login_hint so the IdP pre-selects that account and skips the chooser. SAML
// has no setCustomParameters, so the hint is simply not applied there.
export function providerForIdp(idp: string, loginHint?: string): FirebaseAuthProvider | null {
  const hinted = <T extends GoogleAuthProvider | OAuthProvider>(p: T): T => {
    if (loginHint) p.setCustomParameters({ login_hint: loginHint })
    return p
  }
  switch (idp) {
    case 'google':
      return hinted(new GoogleAuthProvider())
    case 'microsoft':
    case 'microsoft.com': {
      // tenant 'common' accepts BOTH Entra work/school (e.g. Sun River's Azure AD)
      // AND personal Microsoft accounts (hotmail/outlook); select_account forces the
      // account picker. setCustomParameters replaces, so login_hint is set here too.
      const p = new OAuthProvider('microsoft.com')
      p.setCustomParameters({
        tenant: 'common',
        prompt: 'select_account',
        ...(loginHint ? { login_hint: loginHint } : {}),
      })
      return p
    }
    case 'apple':
    case 'apple.com':
      return hinted(new OAuthProvider('apple.com'))
    default:
      if (idp.startsWith('saml.')) return new SAMLAuthProvider(idp)
      if (idp.startsWith('oidc.')) return hinted(new OAuthProvider(idp))
      return null
  }
}

interface AuthContextValue {
  user: User | null
  loading: boolean
  /** Membership role from the bootstrap-stamped custom claim, once resolved. */
  role: Role | null
  /** Org id from the custom claim (the user's home org). */
  orgId: string | null
  /** True for QH/Quantino operators (qh_admin+). */
  isOperator: boolean
  /** True when the user may administer providers (org_admin+). UX-gating only. */
  isAdmin: boolean
  /** i18n key for an error surfaced on return from a failed redirect, or null. */
  authError: string | null
  clearAuthError: () => void
  /** Operator act-as selection (the client org being viewed), or null. */
  actingAsOrg: OrgOption | null
  /** Select (or clear) the act-as org. Operator-only; persisted + sent as X-Org-Context. */
  setActingAsOrg: (org: OrgOption | null) => void
  /** Resolve email/slug → tenant (pre-auth home-realm discovery). */
  resolveTenant: (email?: string, slug?: string) => Promise<TenantResolution>
  /** Pin the SDK to `tenantId`, then redirect to `idp`. Navigates away on success.
   *  `loginHint` (the typed email) pre-selects the account at the IdP. */
  signInWithIdp: (idp: string, tenantId: string, loginHint?: string) => Promise<void>
  signInWithPassword: (email: string, password: string, tenantId: string, newUser?: boolean) => Promise<void>
  signOutUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [role, setRole] = useState<Role | null>(null)
  const [orgId, setOrgId] = useState<string | null>(null)
  const [authError, setAuthError] = useState<string | null>(null)
  const [actingAsOrg, setActingAsOrgState] = useState<OrgOption | null>(null)

  useEffect(() => {
    // Unconfigured Firebase → onAuthStateChanged never fires; resolve to
    // signed-out immediately so the app renders LoginPage instead of hanging.
    if (!isFirebaseConfigured) {
      setLoading(false)
      return
    }

    // Track the returning redirect so we don't flash /login while it completes:
    // onAuthStateChanged can fire with null before getRedirectResult restores the
    // session. We hold the loader until the redirect settles, then only fall through
    // to signed-out if there's still no user.
    let currentUser: User | null = null
    let redirectSettled = false

    // Complete a federated redirect if we're returning from one. The tenant pin
    // was re-applied from localStorage at module load (see firebase.ts) BEFORE
    // this runs — mandatory for GCIP. Only clear the pending pin once the
    // round-trip is actually over (non-null result = success, throw = failure);
    // a null result means this is a normal load (not a redirect return), so we
    // must NOT clear — that would race with, and wipe, a pin a fresh sign-in
    // click just set.
    getRedirectResult(auth)
      .then((result) => {
        if (result) clearPendingTenant()
      })
      .catch((err: unknown) => {
        setAuthError(authErrorKey(err))
        clearPendingTenant()
      })
      .finally(() => {
        redirectSettled = true
        // Redirect done and still no user → genuinely signed out; release the loader.
        if (currentUser === null) setLoading(false)
      })

    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      currentUser = u
      setUser(u)
      if (u) {
        try {
          // Cached claim first (no network round-trip).
          const result = await getIdTokenResult(u)
          if (isRole(result.claims['role'])) {
            setRole(result.claims['role'])
            setOrgId(readOrgIdClaim(result.claims))
          } else {
            // First federated login: bind the uid + stamp claims, then
            // force-refresh the token to pick them up.
            await bootstrapSession()
            const refreshed = await getIdTokenResult(u, true)
            if (isRole(refreshed.claims['role'])) {
              setRole(refreshed.claims['role'])
              setOrgId(readOrgIdClaim(refreshed.claims))
            } else {
              setRole(null)
              setOrgId(null)
            }
          }
        } catch {
          // Bootstrap 401/403, network error, or refresh failure → AccessDenied
          // (correct for an account not provisioned for any org).
          setRole(null)
          setOrgId(null)
        }
        setLoading(false)
      } else {
        setRole(null)
        setOrgId(null)
        // Signed out: hold the loader (don't flash /login) until the returning
        // redirect has settled. If it never settles with a user, the getRedirectResult
        // .finally above releases the loader.
        if (redirectSettled) setLoading(false)
      }
    })
    return unsubscribe
  }, [])

  // Restore (or drop) the persisted act-as selection once the role is known.
  // Operators get their selection re-applied across reloads; for non-operators
  // (or after sign-out) we make sure no act-as context lingers.
  useEffect(() => {
    if (!roleIsOperator(role)) {
      if (getApiOrgContext()) setApiOrgContext(null)
      return
    }
    try {
      const raw = sessionStorage.getItem(ACTING_AS_STORAGE_KEY)
      if (!raw) return
      const org = JSON.parse(raw) as OrgOption
      if (org && typeof org.id === 'string') {
        setActingAsOrgState(org)
        setApiOrgContext(org.id)
      }
    } catch {
      // malformed / sessionStorage unavailable — ignore.
    }
  }, [role])

  // Select (or clear, with null) the org an operator is acting as. Guarded to
  // operators (the backend also 403s a non-operator that sends X-Org-Context).
  // Persisted so it survives reload; cleared on sign-out and on switch-to-none.
  function setActingAsOrg(org: OrgOption | null): void {
    if (org && !roleIsOperator(role)) return
    setActingAsOrgState(org)
    setApiOrgContext(org?.id ?? null)
    try {
      if (org) sessionStorage.setItem(ACTING_AS_STORAGE_KEY, JSON.stringify(org))
      else sessionStorage.removeItem(ACTING_AS_STORAGE_KEY)
    } catch {
      // sessionStorage unavailable — in-memory selection still applies.
    }
  }

  async function signInWithIdp(idp: string, tenantId: string, loginHint?: string): Promise<void> {
    const provider = providerForIdp(idp, loginHint)
    if (!provider) {
      throw new Error(`Unsupported identity provider: ${idp}`)
    }
    // Pin the SDK to the resolved tenant BEFORE the redirect — setting it after
    // is a silent no-op. Persist it (localStorage, for cross-tab/new-window
    // durability across the round-trip); firebase.ts re-applies it on return and
    // getRedirectResult clears it.
    ;(auth as Auth).tenantId = tenantId
    try {
      localStorage.setItem(TENANT_STORAGE_KEY, tenantId)
    } catch {
      // localStorage unavailable — the in-memory pin still covers same-tab return.
    }
    // Navigates away on success; rejects only on a config error (caught by caller).
    await signInWithRedirect(auth, provider)
  }

  // Pin the tenant, then sign in / sign up with email+password (direct, not a
  // redirect — the onAuthStateChanged listener runs bootstrap afterward). newUser
  // sets the password on first login for an invited member (tenant password sign-up
  // is enabled); otherwise a normal password sign-in.
  async function signInWithPassword(email: string, password: string, tenantId: string, newUser = false): Promise<void> {
    ;(auth as Auth).tenantId = tenantId
    try {
      localStorage.setItem(TENANT_STORAGE_KEY, tenantId)
    } catch {
      // localStorage unavailable — the in-memory pin covers this tab.
    }
    if (newUser) {
      await createUserWithEmailAndPassword(auth, email, password)
    } else {
      await signInWithEmailAndPassword(auth, email, password)
    }
  }

  async function signOutUser(): Promise<void> {
    await signOut(auth)
    // Reset the tenant pin (instance + persisted) so the next sign-in
    // re-resolves cleanly from the email step, and drop any act-as selection.
    ;(auth as Auth).tenantId = null
    setActingAsOrgState(null)
    setApiOrgContext(null)
    clearPendingTenant()
    try {
      sessionStorage.removeItem(ACTING_AS_STORAGE_KEY)
    } catch {
      // sessionStorage unavailable — nothing to clear.
    }
  }

  const value: AuthContextValue = {
    user,
    loading,
    role,
    orgId,
    isOperator: roleIsOperator(role),
    isAdmin: isOrgAdmin(role),
    authError,
    clearAuthError: () => setAuthError(null),
    actingAsOrg,
    setActingAsOrg,
    resolveTenant,
    signInWithIdp,
    signInWithPassword,
    signOutUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}

// Reads the org id claim, tolerating snake/camel drift in the stamped claim.
function readOrgIdClaim(claims: Record<string, unknown>): string | null {
  const v = claims['org_id'] ?? claims['orgId']
  return typeof v === 'string' ? v : null
}

// Maps Firebase auth error codes (and the resolver 400) to i18n keys used by the
// Login page — for the resolver step and for errors surfaced on return from a
// failed federated redirect.
export function authErrorKey(err: unknown): string {
  // Resolver 400 — malformed email. (Unknown domains return a uniform 200 with
  // an empty tenantId, handled as the org-not-found path in the LoginPage.)
  if (err !== null && typeof err === 'object' && 'status' in err && (err as { status: unknown }).status === 400) {
    return 'login.invalidEmail'
  }

  const code = typeof err === 'object' && err !== null && 'code' in err
    ? (err as { code: string }).code
    : ''

  switch (code) {
    case 'auth/unauthorized-domain':
      return 'login.unauthorizedDomain'
    case 'auth/account-exists-with-different-credential':
      return 'login.accountConflict'
    case 'auth/network-request-failed':
      return 'login.networkError'
    case 'auth/too-many-requests':
      return 'login.tooManyRequests'
    case 'auth/user-disabled':
      return 'login.accountDisabled'
    case 'auth/user-cancelled':
    case 'auth/cancelled-popup-request':
      return 'login.signInCancelled'
    case 'auth/user-not-found':
      return 'login.userNotFound'
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
      return 'login.wrongPassword'
    case 'auth/email-already-in-use':
      return 'login.emailInUse'
    case 'auth/weak-password':
      return 'login.passwordTooShort'
    default:
      return 'login.genericError'
  }
}
